import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  BringUpCreateForm,
  StatusNotice,
} from './bringUpShared';

type ScopeInfo = {
  orgId?: string;
  siteId?: string;
};

function StatusNoticeDetails({
  notice,
  onClose,
}: {
  notice: StatusNotice;
  onClose?: () => void;
}) {
  return (
    <Alert severity={notice.level} sx={{ mt: 1.5 }} onClose={onClose}>
      <Box>
        <Typography variant="body2">{notice.message}</Typography>
        {Array.isArray(notice.facts) && notice.facts.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Failure details
            </Typography>
            {notice.facts.map((fact) => (
              <Typography key={`${fact.label}:${fact.value}`} variant="body2">
                <strong>{fact.label}:</strong> {fact.value}
              </Typography>
            ))}
          </Box>
        )}
        {notice.technical && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Technical diagnostics
            </Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.25,
                borderRadius: 1,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                bgcolor: 'rgba(0, 0, 0, 0.08)',
              }}
            >
              {notice.technical}
            </Box>
          </Box>
        )}
      </Box>
    </Alert>
  );
}

type BringUpCreateDeviceDialogProps = {
  open: boolean;
  busy: boolean;
  error: string | null;
  form: BringUpCreateForm;
  setForm: React.Dispatch<React.SetStateAction<BringUpCreateForm>>;
  canWriteDevices: boolean;
  uiScope: ScopeInfo;
  onClose: () => void;
  onClearError: () => void;
  onCreate: () => void;
};

export function BringUpCreateDeviceDialog({
  open,
  busy,
  error,
  form,
  setForm,
  canWriteDevices,
  uiScope,
  onClose,
  onClearError,
  onCreate,
}: BringUpCreateDeviceDialogProps) {
  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add New Device</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Device will be created under scope: <strong>{uiScope.orgId || '(unset org)'}</strong> /{' '}
          <strong>{uiScope.siteId || '(unset site)'}</strong>
        </Alert>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Device ID"
              value={form.device_id}
              onChange={(e) => setForm((prev) => ({ ...prev, device_id: e.target.value }))}
              disabled={busy}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Device Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              disabled={busy}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Device Type</InputLabel>
              <Select
                value={form.device_type}
                label="Device Type"
                onChange={(e) => setForm((prev) => ({ ...prev, device_type: String(e.target.value || 'edge') }))}
                disabled={busy}
              >
                <MenuItem value="edge">Edge Device</MenuItem>
                <MenuItem value="camera">Camera</MenuItem>
                <MenuItem value="sensor">Sensor</MenuItem>
                <MenuItem value="gateway">Gateway</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Location (optional)"
              value={form.location}
              onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
              disabled={busy}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description (optional)"
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              disabled={busy}
            />
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={onClearError}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onCreate} disabled={!canWriteDevices || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type BringUpDangerZoneProps = {
  selectedDeviceId: string;
  canWriteDevices: boolean;
  decommissionBusy: boolean;
  decommissionConfirm: string;
  onConfirmChange: (value: string) => void;
  onFillConfirm: () => void;
  deleteAfterDecommission: boolean;
  onDeleteAfterChange: (checked: boolean) => void;
  factoryResetOnDecommission: boolean;
  onFactoryResetChange: (checked: boolean) => void;
  deleteConfigOnDecommission: boolean;
  onDeleteConfigChange: (checked: boolean) => void;
  decommissionResult: StatusNotice | null;
  onClearResult: () => void;
  jobIsRunning: boolean;
  onDecommission: () => void;
  onDeleteDeviceOnly: () => void;
};

export function BringUpDangerZone({
  selectedDeviceId,
  canWriteDevices,
  decommissionBusy,
  decommissionConfirm,
  onConfirmChange,
  onFillConfirm,
  deleteAfterDecommission,
  onDeleteAfterChange,
  factoryResetOnDecommission,
  onFactoryResetChange,
  deleteConfigOnDecommission,
  onDeleteConfigChange,
  decommissionResult,
  onClearResult,
  jobIsRunning,
  onDecommission,
  onDeleteDeviceOnly,
}: BringUpDangerZoneProps) {
  const confirmMatches =
    String(decommissionConfirm || '').trim() === String(selectedDeviceId || '').trim();

  return (
    <Paper sx={{ p: 2, mt: 2, border: '1px dashed', borderColor: 'error.main' }}>
      <Typography variant="h6" color="error" gutterBottom>
        Danger Zone (Not Part Of Bring-up)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Revokes edge credentials immediately and sends decommission intent to the unit. Use only when wiping or
        retiring a device.
      </Typography>
      <Alert severity="info" sx={{ mb: 1.5 }}>
        `Delete Device` below removes only the cloud registry row. It does not touch the edge unit.
      </Alert>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack direction="row" gap={1} alignItems="flex-start">
            <TextField
              fullWidth
              size="small"
              label={`Type ${selectedDeviceId || 'device_id'} to confirm`}
              value={decommissionConfirm}
              onChange={(e) => onConfirmChange(e.target.value)}
              disabled={!canWriteDevices || !selectedDeviceId || decommissionBusy}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={onFillConfirm}
              disabled={!canWriteDevices || !selectedDeviceId || decommissionBusy}
              sx={{ mt: 0.5, flexShrink: 0 }}
            >
              Fill
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={deleteAfterDecommission}
                onChange={(_, checked) => onDeleteAfterChange(checked)}
                disabled={!canWriteDevices || decommissionBusy}
              />
            }
            label="Also delete device record from cloud after decommission completes"
          />
          <FormControlLabel
            control={
              <Switch
                checked={factoryResetOnDecommission}
                onChange={(_, checked) => onFactoryResetChange(checked)}
                disabled={!canWriteDevices || decommissionBusy}
              />
            }
            label="Factory reset edge (wipe /var/edgentik/state/*)"
          />
          <FormControlLabel
            control={
              <Switch
                checked={deleteConfigOnDecommission}
                onChange={(_, checked) => onDeleteConfigChange(checked)}
                disabled={!canWriteDevices || decommissionBusy}
              />
            }
            label="Also delete device-scoped cloud config (zones/policies)"
          />
        </Grid>
      </Grid>
      {decommissionResult && <StatusNoticeDetails notice={decommissionResult} onClose={onClearResult} />}
      <Stack direction="row" gap={1} mt={2}>
        <Button
          color="error"
          variant="contained"
          onClick={onDecommission}
          disabled={!canWriteDevices || !selectedDeviceId || decommissionBusy || jobIsRunning || !confirmMatches}
        >
          {decommissionBusy ? 'Decommissioning…' : 'Decommission Device'}
        </Button>
        <Button
          color="warning"
          variant="outlined"
          onClick={onDeleteDeviceOnly}
          disabled={!canWriteDevices || !selectedDeviceId || decommissionBusy || jobIsRunning || !confirmMatches}
        >
          Delete Device (Cloud Only)
        </Button>
      </Stack>
    </Paper>
  );
}

type BringUpGuidedRecoveryDialogProps = {
  open: boolean;
  selectedDeviceId: string;
  canWriteDevices: boolean;
  guidedRecoveryBusy: boolean;
  guidedRecoveryDeleteAfter: boolean;
  onDeleteAfterChange: (checked: boolean) => void;
  guidedRecoveryRetryBringUp: boolean;
  onRetryBringUpChange: (checked: boolean) => void;
  guidedRecoveryError: StatusNotice | null;
  onClearError: () => void;
  onClose: () => void;
  onRun: () => void;
};

export function BringUpGuidedRecoveryDialog({
  open,
  selectedDeviceId,
  canWriteDevices,
  guidedRecoveryBusy,
  guidedRecoveryDeleteAfter,
  onDeleteAfterChange,
  guidedRecoveryRetryBringUp,
  onRetryBringUpChange,
  guidedRecoveryError,
  onClearError,
  onClose,
  onRun,
}: BringUpGuidedRecoveryDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Guided Recovery</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This will revoke edge credentials, request a factory reset, and wipe device-scoped cloud config for{' '}
          <strong>{selectedDeviceId || '(no device selected)'}</strong>.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use this when Bring Up reports stale identity or stale scope. By default the recovery flow recreates the
          selected cloud device and restarts Bring Up automatically after the wipe is confirmed.
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={guidedRecoveryRetryBringUp}
              onChange={(_, checked) => onRetryBringUpChange(checked)}
              disabled={!canWriteDevices || guidedRecoveryBusy || guidedRecoveryDeleteAfter}
            />
          }
          label="Automatically recreate the selected device and restart Bring Up after the wipe"
        />
        <FormControlLabel
          control={
            <Switch
              checked={guidedRecoveryDeleteAfter}
              onChange={(_, checked) => onDeleteAfterChange(checked)}
              disabled={!canWriteDevices || guidedRecoveryBusy}
            />
          }
          label="Leave the selected cloud device deleted after recovery"
        />
        {guidedRecoveryError && <StatusNoticeDetails notice={guidedRecoveryError} onClose={onClearError} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guidedRecoveryBusy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onRun}
          disabled={!canWriteDevices || !selectedDeviceId || guidedRecoveryBusy}
        >
          {guidedRecoveryBusy ? 'Running Recovery…' : 'Run Guided Recovery'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
